import React, { useMemo, useState } from 'react';
import {
	CaseType,
	CaseTypeType,
	useWorkplaceRootCaseTypesQuery,
	useWorkplacesQuery,
	WorkplaceCaseType,
} from '../../../../graphql/graphql';
import MonitoringCenterNewRequestsItem from './MonitoringCenterNewRequestsItem';
import styles from './MonitoringCenterNewRequests.module.scss';
import MonitoringCenterNewRequestsFilter, {
	MonitoringCenterNewRequestsFilterValues,
} from './MonitoringCenterNewRequestsFilter';
import MonitoringCenterNewRequestsItemDetail from './MonitoringCenterNewRequestsItemDetail';
import { CustomSettingKey } from '../../../Base/Enums/CustomSettingKey.enum';
import MonitoringCenterSettingsPanel from '../MonitoringCenterSettings/MonitoringCenterSettingsPanel';
import useLoading from '../../../Base/Hooks/useLoading';
import { CustomSettingsContextProvider } from '../../../CustomSettings/Services/CustomSettingsContext';

const MonitoringCenterNewRequests: React.FC = () => {
	const { RenderLoading } = useLoading();
	const [selectedCaseType, setSelectedCaseType] = useState<CaseType>();
	const [filter, setFilter] =
		useState<MonitoringCenterNewRequestsFilterValues>();

	const userWorkplaces = useWorkplacesQuery();

	const workplaceIds = useMemo(() => {
		const tempIds: string[] = [];
		userWorkplaces?.data?.workplaces?.data?.forEach(item => {
			if (item?.id) {
				tempIds.push(item.id);
			}
		});
		return tempIds;
	}, [userWorkplaces]);

	const caseTypes = useWorkplaceRootCaseTypesQuery({
		workplaceIds: workplaceIds,
		caseTypeTypes: [CaseTypeType.CaseType, CaseTypeType.Folder],
	});

	const groupedCaseTypes = useMemo(() => {
		const tempCaseTypes: WorkplaceCaseType[] = [];
		caseTypes?.data?.workplaceCaseTypes?.data?.forEach(item => {
			if (
				item &&
				!tempCaseTypes.some(
					subItem => subItem?.caseType?.code === item?.caseType?.code,
				)
			) {
				tempCaseTypes.push(item);
			}
		});
		return tempCaseTypes;
	}, [caseTypes]);

	const handleChangeFilter = (
		filter: MonitoringCenterNewRequestsFilterValues,
	) => {
		setFilter(filter);
	};

	const handleOpenDetail = (caseType: CaseType) => {
		setSelectedCaseType(caseType);
	};

	if (caseTypes.isLoading || userWorkplaces.isLoading) {
		return <RenderLoading />;
	}

	return (
		<CustomSettingsContextProvider
			customSettingsKey={CustomSettingKey.MONITORING_CENTER_NEW_REQUESTS}>
			<div className={styles.root}>
				<MonitoringCenterSettingsPanel
					customSettingKey={CustomSettingKey.MONITORING_CENTER_NEW_REQUESTS}
				/>
				<MonitoringCenterNewRequestsFilter onFilter={handleChangeFilter} />
				{selectedCaseType ? (
					<MonitoringCenterNewRequestsItemDetail
						caseType={selectedCaseType}
						onBack={() => setSelectedCaseType(undefined)}
						filter={filter}
					/>
				) : (
					<div className={styles.wrapper}>
						{groupedCaseTypes
							.filter(
								(item): item is typeof item & { caseType: CaseType } =>
									item.caseType !== undefined,
							)
							.map((item, index) => (
								<React.Fragment key={index}>
									<MonitoringCenterNewRequestsItem
										filter={filter}
										caseType={item.caseType}
										onShowMore={handleOpenDetail}
										style={{ overflow: 'initial' }}
									/>
								</React.Fragment>
							))}
					</div>
				)}
			</div>
		</CustomSettingsContextProvider>
	);
};

export default MonitoringCenterNewRequests;
